import { useEffect, useState } from 'react';
import { planService } from '../services/api';

const formatStripePrice = (price) => {
  const amount = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: (price.currency || 'eur').toUpperCase(),
  }).format((price.unit_amount || 0) / 100);
  if (!price.recurring) return `${amount} una tantum`;
  const count = price.recurring.interval_count || 1;
  return `${amount} / ${count > 1 ? `${count} ` : ''}${price.recurring.interval}`;
};

export default function StripeProductPriceSelector({
  mode = 'prices',
  priceType = 'all',
  selectedPriceIds = [],
  initialPrices = [],
  onPriceChange,
  disabled = false,
  maxHeight = 250,
  singleProduct = false,
  singleProductErrorMessage = '',
}) {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [knownPrices, setKnownPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const priceSelection = mode === 'prices' || mode === 'products';
  const multiplePrices = priceType !== 'one_time';

  useEffect(() => {
    if (!initialPrices.length) return;
    setKnownPrices((previous) => {
      const next = { ...previous };
      initialPrices.forEach((price) => {
        const priceId = price.id || price.stripePriceId;
        if (!priceId) return;
        next[priceId] = price.recurring
          ? price
          : {
              ...price,
              id: priceId,
              recurring: price.interval
                ? { interval: price.interval, interval_count: price.intervalCount || 1 }
                : null,
            };
      });
      return next;
    });
  }, [initialPrices]);

  const loadProducts = async (query = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await planService.listStripeProducts(query);
      const loadedProducts = response.data || [];
      setProducts(loadedProducts);
      setKnownPrices((previous) => {
        const next = { ...previous };
        loadedProducts.forEach((product) => {
          (product.prices || []).forEach((price) => {
            next[price.id] = { ...price, stripeProductId: product.id };
          });
        });
        return next;
      });
    } catch (loadError) {
      setError(loadError.response?.data?.message || 'Impossibile caricare il catalogo Stripe.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProducts(''); }, []);

  const getVisiblePrices = (product) => (product.prices || [])
    .filter((price) => {
      if (priceType === 'recurring') return Boolean(price.recurring);
      if (priceType === 'one_time') return !price.recurring;
      return true;
    })
    .map((price) => ({ ...price, stripeProductId: product.id }));

  const getRecurringKey = (price) => (
    price?.recurring
      ? `${price.recurring.interval}:${Number(price.recurring.interval_count || 1)}`
      : null
  );

  const formatRecurringSchedule = (price) => {
    const interval = price?.recurring?.interval;
    const count = Number(price?.recurring?.interval_count || 1);
    const singular = { day: 'giornaliero', week: 'settimanale', month: 'mensile', year: 'annuale' };
    const plural = { day: 'giorni', week: 'settimane', month: 'mesi', year: 'anni' };
    return count === 1 ? singular[interval] || interval : `ogni ${count} ${plural[interval] || interval}`;
  };

  const findKnownPrice = (priceId) => knownPrices[priceId]
    || products.flatMap((product) => (product.prices || []).map((price) => ({
      ...price,
      stripeProductId: product.id,
    }))).find((price) => price.id === priceId);

  const getStripeProductId = (price) => {
    if (price?.stripeProductId) return price.stripeProductId;
    if (typeof price?.product === 'string') return price.product;
    return price?.product?.id || null;
  };

  const getSelectedProductId = () => selectedPriceIds
    .map((priceId) => getStripeProductId(findKnownPrice(priceId)))
    .find(Boolean);

  const setProductMismatchError = (candidateProductId, selectedProductId) => {
    const explanation = singleProductErrorMessage
      || 'I prezzi selezionati per lo stesso ruolo economico devono appartenere allo stesso Product Stripe.';
    setSelectionError(
      `${explanation} Product già selezionato: ${selectedProductId}. Il nuovo Price appartiene a: ${candidateProductId}.`
    );
  };

  const emitPriceChange = (priceIds) => {
    onPriceChange(priceIds, priceIds.map(findKnownPrice).filter(Boolean));
  };

  const togglePrice = (priceId) => {
    setSelectionError('');
    if (!multiplePrices) {
      emitPriceChange(selectedPriceIds.includes(priceId) ? [] : [priceId]);
      return;
    }
    if (selectedPriceIds.includes(priceId)) {
      emitPriceChange(selectedPriceIds.filter((id) => id !== priceId));
      return;
    }

    const candidate = findKnownPrice(priceId);
    const candidateProductId = getStripeProductId(candidate);
    const selectedProductId = getSelectedProductId();
    if (singleProduct && selectedProductId && candidateProductId && candidateProductId !== selectedProductId) {
      setProductMismatchError(candidateProductId, selectedProductId);
      return;
    }
    const candidateKey = getRecurringKey(candidate);
    const conflictingPriceId = selectedPriceIds.find((selectedId) => (
      getRecurringKey(findKnownPrice(selectedId)) === candidateKey
    ));
    if (candidateKey && conflictingPriceId) {
      setSelectionError(
        `È già selezionato un prezzo ${formatRecurringSchedule(candidate)} (${conflictingPriceId}). Deselezionalo prima di sceglierne un altro con la stessa periodicità.`
      );
      return;
    }

    emitPriceChange([...selectedPriceIds, priceId]);
  };

  const toggleAllProductPrices = (prices) => {
    setSelectionError('');
    const productPriceIds = prices.map((price) => price.id);
    const allSelected = productPriceIds.every((priceId) => selectedPriceIds.includes(priceId));
    if (allSelected) {
      emitPriceChange(selectedPriceIds.filter((priceId) => !productPriceIds.includes(priceId)));
      return;
    }

    const candidateProductId = getStripeProductId(prices[0]);
    const selectedProductId = getSelectedProductId();
    if (singleProduct && selectedProductId && candidateProductId && candidateProductId !== selectedProductId) {
      setProductMismatchError(candidateProductId, selectedProductId);
      return;
    }

    const nextSelectedIds = [...selectedPriceIds];
    const selectedKeys = new Set(nextSelectedIds.map((priceId) => getRecurringKey(findKnownPrice(priceId))).filter(Boolean));
    const skippedSchedules = [];
    prices.forEach((price) => {
      if (nextSelectedIds.includes(price.id)) return;
      const recurringKey = getRecurringKey(price);
      if (recurringKey && selectedKeys.has(recurringKey)) {
        skippedSchedules.push(formatRecurringSchedule(price));
        return;
      }
      nextSelectedIds.push(price.id);
      if (recurringKey) selectedKeys.add(recurringKey);
    });

    emitPriceChange(nextSelectedIds);
    if (skippedSchedules.length > 0) {
      setSelectionError(
        `Selezione parziale: ${Array.from(new Set(skippedSchedules)).join(', ')} già presente. È ammesso un solo prezzo per periodicità.`
      );
    }
  };

  return (
    <div style={{ marginTop: 10, border: '1px solid #dbeafe', borderRadius: 8, padding: 10, background: '#f8fbff' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          value={search}
          disabled={disabled}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); loadProducts(search); } }}
          placeholder="Cerca prodotto Stripe"
          style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }}
        />
        <button type="button" className="btn-secondary" disabled={disabled || loading} onClick={() => loadProducts(search)}>
          {loading ? 'Caricamento...' : 'Cerca'}
        </button>
      </div>
      {error && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {selectionError && (
        <div role="alert" style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 9px', fontSize: 12, marginBottom: 8 }}>
          {selectionError}
        </div>
      )}
      <div style={{ maxHeight, overflowY: 'auto' }}>
        {products.map((product) => {
          const prices = getVisiblePrices(product);
          if (!prices.length) return null;
          const allPricesSelected = prices.every((price) => selectedPriceIds.includes(price.id));
          const somePricesSelected = prices.some((price) => selectedPriceIds.includes(price.id));
          return (
            <div key={product.id} style={{ marginBottom: 8, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', background: '#f8fafc', fontWeight: 700, fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={allPricesSelected}
                  aria-checked={somePricesSelected && !allPricesSelected ? 'mixed' : allPricesSelected}
                  disabled={disabled}
                  title={priceSelection ? 'Seleziona tutti i prezzi compatibili del prodotto' : 'Importa tutti i prezzi del prodotto'}
                  onChange={() => toggleAllProductPrices(prices)}
                />
                <span>{product.name}</span>
                <code style={{ color: '#94a3b8' }}>{product.id}</code>
              </label>
              {prices.map((price) => {
                const selected = selectedPriceIds.includes(price.id);
                return (
                  <label
                    key={price.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 20, padding: '7px 9px 7px 12px', borderLeft: '2px solid #dbeafe', cursor: disabled ? 'not-allowed' : 'pointer', background: selected ? '#eff6ff' : '#fff' }}
                  >
                    <input
                      type={priceSelection && !multiplePrices ? 'radio' : 'checkbox'}
                      name={priceSelection && !multiplePrices ? `stripe-${priceType}-price` : undefined}
                      checked={selected}
                      disabled={disabled}
                      onChange={() => togglePrice(price.id)}
                    />
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{formatStripePrice(price)}</span>
                    <code style={{ marginLeft: 'auto', color: '#64748b', fontSize: 11 }}>{price.id}</code>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
